// pages/[collection]/category/index.jsx
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import Head from 'next/head';
import BaseLayout from '../../../layouts/BaseLayout.jsx';

const CollectionCategoriesPage = () => {
  const router = useRouter();
  const { collection } = router.query;

  const [categoriesData, setCategoriesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Collection URL mappings
  const collectionUrlMap = {
    basic: {
      baseUrl: 'https://icons-pbl.davooda.com/basic-icons/',
      imageSuffix: '.svg',
      type: 'svg',
      urlPrefix: '/basic-icons',
    },
    outline: {
      baseUrl: 'https://icons-pbl.davooda.com/outline/png-256px/',
      imageSuffix: '-256x256.png',
      type: 'png',
      urlPrefix: '/outline',
    },
    'polish-blue': {
      baseUrl: 'https://icons-pbl.davooda.com/polish-blue/png-256px/',
      imageSuffix: '-256x256.png',
      type: 'png',
      urlPrefix: '/polish-blue-icons',
    },
    'basic3d': {
      baseUrl: 'https://icons-pbl.davooda.com/basic3d-icons/png-256px/',
      imageSuffix: '.png',
      type: 'png',
      urlPrefix: '/basic3d',
    },
  };

  // URL collection mappings for API calls
  const urlCollectionMap = {
    'basic-icons': 'basic',
    'outline-icons': 'outline',
    'polish-blue-icons': 'polish-blue',
    'basic3d': 'basic3d',
  };

  // Helper function to get collection display name
  const getCollectionDisplayName = (collectionKey) => {
    switch (collectionKey) {
      case 'basic-icons':
      case 'basic':
        return 'Basic Collection';
      case 'outline':
        return 'Outline Collection';
      case 'polish-blue-icons':
      case 'polish-blue':
        return 'Polish Blue Collection';
      case 'basic3d':
        return 'Basic 3D Collection';
      default:
        return `${collectionKey} Collection`;
    }
  };

  // Helper function to get collection URL
  const getCollectionUrl = (collection) => {
    switch (collection) {
      case 'basic-icons':
      case 'basic':
        return '/basic-icons/';
      case 'outline':
        return '/outline/category/';
      case 'polish-blue-icons':
      case 'polish-blue':
        return '/polish-blue-icons/';
      case 'basic3d':
        return '/basic3d/';
      default:
        return '/';
    }
  };

  // Get actual collection name for API call
  const getApiCollectionName = (urlCollection) => {
    return urlCollectionMap[urlCollection] || urlCollection;
  };

  // Generate BreadcrumbList schema
  const getBreadcrumbsJSON = () => {
    const collectionDisplayName = getCollectionDisplayName(collection);
    const collectionUrl = getCollectionUrl(collection);

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Icons',
          item: 'https://davooda.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: collectionDisplayName,
          item: `https://davooda.com${collectionUrl}`,
        }
      ],
    };
  };

  // Process asset for display
  const processAsset = (asset) => {
    if (!asset) return null;

    const assetCollection = asset.collection || 'Unknown';
    const isProt = assetCollection.startsWith('prot-');
    const mapKey = isProt ? `prot-${assetCollection.split('prot-')[1]}` : assetCollection;

    let src = '';
    if (collectionUrlMap[mapKey]) {
      src = `${collectionUrlMap[mapKey].baseUrl}${asset.slug}${collectionUrlMap[mapKey].imageSuffix}`;
    } else if (asset.url_small) {
      src = asset.url_small;
    } else {
      src = '/images/placeholder.png';
    }

    let url = '/icons';
    if (assetCollection === 'basic3d') {
      url = `/basic3d/${asset.slug}`;
    } else if (collectionUrlMap[mapKey] && collectionUrlMap[mapKey].urlPrefix) {
      url = `${collectionUrlMap[mapKey].urlPrefix}/${asset.slug}`;
    } else {
      url = asset.media_type === 'icon' ? `/icon/${asset.slug}` : `/photo/${asset.slug}`;
    }

    return {
      src: src,
      alt: asset.title || asset.slug || 'Asset',
      key: asset.slug || `asset-${asset.id}`,
      url: url,
      isFree: asset.is_free,
      collection: assetCollection,
      title: asset.title,
      type: collectionUrlMap[mapKey]?.type || 'png'
    };
  };

  // Check if icon is from basic collection (for SVG specific styling)
  const isBasicSvgIconCollection = (collectionName) => {
    return collectionName === 'basic-icons';
  };

  // Fetch categories data
  useEffect(() => {
    if (!collection) return;

    const fetchCategoriesData = async () => {
      try {
        setLoading(true);
        setError(null);
        const apiCollection = getApiCollectionName(collection);

        const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/categories_list`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            collection: apiCollection,
            preview_limit: 10
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch categories data' }));
          throw new Error(errorData.detail || `HTTP error ${response.status}`);
        }

        const data = await response.json();
        setCategoriesData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoriesData();
  }, [collection]);

  if (loading) {
    return (
      <BaseLayout
        title="Loading..."
        description="Loading categories"
        showHeader={true}
      >
        <div className="page-container-with-top-margin">
          <div className="max-w-7xl w-[90%] mx-auto py-8">
            <div className="text-center">Loading...</div>
          </div>
        </div>
      </BaseLayout>
    );
  }

  if (error) {
    return (
      <BaseLayout
        title="Error"
        description="Error loading categories"
        showHeader={true}
      >
        <div className="page-container-with-top-margin">
          <div className="max-w-7xl w-[90%] mx-auto py-8">
            <div className="text-center text-red-500">Error: {error}</div>
          </div>
        </div>
      </BaseLayout>
    );
  }

  const collectionDisplayName = getCollectionDisplayName(collection);
  const categories = categoriesData?.categories || [];

  return (
    <BaseLayout
      title={`${collectionDisplayName} Categories`}
      description={`Browse all categories in ${collectionDisplayName} collection`}
      showHeader={true}
    >
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(getBreadcrumbsJSON()),
          }}
        />
      </Head>

      {/* Styles for icons */}
      <style jsx>{`
        .basic3d-icon {
          max-width: 65% !important;
          max-height: 65% !important;
          width: 65% !important;
          height: 65% !important;
          transform: scale(1.3);
        }
        .standard-icon {
          max-width: 50%;
          max-height: 50%;
        }
        .basic-svg-icon {
          width: 50%;
          height: 50%;
          object-fit: contain;
        }
        .basic3d-container {
          padding: 0 !important;
        }
        .icon-container {
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>

      <div className="page-container-with-top-margin">
        <div className="max-w-7xl mx-auto py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">{collectionDisplayName}</h1>
          </div>

          {/* Categories Grid - 2 columns */}
          {categories.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-10">
              {categories.map((category) => (
                <div key={category.category_slug}>
                  {/* Category info - entire area as link */}
                  <Link
                    href={`/${collection}/category/${category.category_slug}`}
                    className="block mb-2 group"
                  >
                    <p className="text-sm text-gray-500 mb-1 group-hover:text-indigo-600 transition-colors duration-200">
                      {category.assets_count} icons
                    </p>
                    <h2 className="text-base font-bold text-gray-900 group-hover:text-indigo-600 transition-colors duration-200">
                      {category.category_name}
                    </h2>
                  </Link>

                  {/* Preview Icons Grid - 5 columns, 2 rows */}
                  {category.preview_assets && category.preview_assets.length > 0 ? (
                    <div className="grid grid-cols-5 gap-2">
                      {category.preview_assets.slice(0, 10).map((asset) => {
                        const processedAsset = processAsset(asset);
                        if (!processedAsset) return null;

                        const isBasic3d = processedAsset.collection === 'basic3d';
                        const isBasicSvg = isBasicSvgIconCollection(processedAsset.collection);

                        return (
                          <div key={processedAsset.key} className="rounded-lg">
                            <Link href={processedAsset.url}>
                              <div className={`icon-container ${isBasic3d ? 'basic3d-container p-0' : 'p-2'} rounded-lg border border-gray-200 aspect-square group transition-transform duration-300 hover:border-gray-300`}>
                                {isBasicSvg ? (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <img
                                      src={processedAsset.src}
                                      alt={processedAsset.alt}
                                      className="basic-svg-icon group-hover:scale-110 transition-transform duration-300"
                                    />
                                  </div>
                                ) : (
                                  <img
                                    src={processedAsset.src}
                                    alt={processedAsset.alt}
                                    width="40"
                                    height="40"
                                    className={`${isBasic3d ? 'basic3d-icon' : 'standard-icon'} group-hover:scale-110 transition-transform duration-300`}
                                    style={{
                                      maxWidth: isBasic3d ? '65% !important' : '50%',
                                      maxHeight: isBasic3d ? '65% !important' : '50%',
                                      width: isBasic3d ? '65% !important' : 'auto',
                                      height: isBasic3d ? '65% !important' : 'auto',
                                    }}
                                  />
                                )}

                                {/* Free badge */}
                                {processedAsset.isFree && (
                                  <div className="absolute top-1 right-1 bg-green-100 text-green-800 text-xs px-1 py-0.5 rounded">
                                    Free
                                  </div>
                                )}
                              </div>
                            </Link>
                          </div>
                        );
                      })}
                      
                      {/* Fill empty slots if less than 10 icons */}
                      {Array.from({ length: Math.max(0, 10 - (category.preview_assets?.length || 0)) }).map((_, index) => (
                        <div key={`empty-${index}`} className="aspect-square rounded-lg border border-gray-100 bg-gray-50"></div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-5 gap-2">
                      {Array.from({ length: 10 }).map((_, index) => (
                        <div key={`placeholder-${index}`} className="aspect-square rounded-lg border border-gray-100 bg-gray-50 flex items-center justify-center">
                          <span className="text-gray-300 text-xs">?</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No categories found in this collection.</p>
            </div>
          )}
        </div>
      </div>
    </BaseLayout>
  );
};

export default CollectionCategoriesPage;